/**
 * AI Assistant Chat
 *
 * Floating chat interface for interacting with the SLIK AI Manager.
 * Admin-only access - managers and owners can chat with the AI about shop operations.
 *
 * Features:
 * - Floating action button (SLIK logo/brain icon)
 * - Expandable chat panel
 * - Voice input option (future)
 * - Context-aware responses about shop data
 */

import React, { useState, useRef, useEffect } from 'react';
import { Brain, X, Send, Loader2, Sparkles, Mic } from 'lucide-react';
import { aiManagerAPI } from '../../services/AIManagerAPI';
import { isOwnerRole } from '../../shared/authority';
import { useAuth } from '../../contexts/AuthContext';
import { usePinLock } from '../../contexts/PinLockContext';
import { barberDataService } from '../../services/barberDataService';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  contextUsed?: string[];
}

interface AIAssistantChatProps {
  isOpen: boolean;
  onClose: () => void;
}

const AIAssistantChat: React.FC<AIAssistantChatProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { currentBarber } = usePinLock();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Check if user has admin permissions (manager or owner)
  // PRIORITY: Station barber (PIN login) takes precedence over Firebase user
  const firebaseRole = (user?.role as string)?.toLowerCase();

  // Get the current barber's role from barberDataService
  const stationBarber = currentBarber?.barberId
    ? barberDataService.getBarberById(currentBarber.barberId)
    : null;
  const stationRole = stationBarber?.role?.toLowerCase();

  // If someone is logged in at the station (PIN), use THEIR role
  // Otherwise fall back to Firebase user role (for when no station login)
  const effectiveRole = currentBarber?.barberId ? stationRole : firebaseRole;

  // Admin only if the ACTIVE user (station barber or Firebase user) is owner/admin
  // Note: "manager" role does NOT have access - only owner and admin
  const isAdmin = isOwnerRole(effectiveRole);

  // Get the display name (prefer station barber name)
  const displayName = currentBarber?.barberName?.split(' ')[0] || user?.firstName || 'Boss';

  // Check AI Manager connection on mount
  useEffect(() => {
    if (!isAdmin) return;
    const checkConnection = async () => {
      const healthy = await aiManagerAPI.checkHealth();
      setIsConnected(healthy);
    };
    checkConnection();
  }, [isAdmin]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Add welcome message when chat opens for first time
  useEffect(() => {
    if (isOpen && messages.length === 0 && isAdmin) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: `Hey ${displayName}! 👋 I'm your AI Manager. Ask me anything about the shop - today's performance, barber stats, client insights, or coaching tips. How can I help?`,
        timestamp: new Date(),
      }]);
    }
  }, [isOpen, messages.length, displayName, isAdmin]);

  // Don't render if not admin
  if (!isAdmin) {
    return null;
  }

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await aiManagerAPI.chat(userMessage.content, {
        context: 'shop_manager_assistant',
      });

      if (response) {
        const assistantMessage: Message = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: response.response,
          timestamp: new Date(),
          contextUsed: response.context_used,
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        // Fallback if API is not available
        const fallbackMessage: Message = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: "I'm having trouble connecting to the AI service right now. Make sure the AI Manager server is running on port 8000. You can start it with `python main.py` in the slik-ai-manager folder.",
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, fallbackMessage]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'system',
        content: 'Something went wrong. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Quick action buttons
  const quickActions = [
    { label: "Today's Overview", query: "How's the shop doing today?" },
    { label: "Top Barbers", query: "Who are my top performing barbers this month?" },
    { label: "At-Risk Clients", query: "Which clients haven't visited in a while?" },
    { label: "Coaching Tips", query: "Give me coaching tips for my team" },
  ];

  const handleQuickAction = (query: string) => {
    setInputValue(query);
    // Auto-send after a brief delay
    setTimeout(() => {
      const input = inputRef.current;
      if (input) {
        input.focus();
      }
    }, 100);
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="fixed bottom-20 md:bottom-6 right-4 z-40 w-[calc(100vw-2rem)] md:w-96
          bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700
          flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
          style={{ maxHeight: 'calc(100vh - 200px)', height: '480px' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-indigo-700 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Brain className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold">SLIK AI Manager</h3>
                <div className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : isConnected === false ? 'bg-red-400' : 'bg-yellow-400'}`} />
                  <span className="text-white/80 text-xs">
                    {isConnected ? 'Connected' : isConnected === false ? 'Offline' : 'Checking...'}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-1"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-800">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-br-md'
                      : message.role === 'system'
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                      : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-sm rounded-bl-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  {message.contextUsed && message.contextUsed.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        Used: {message.contextUsed.join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-gray-700 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions (only show when no messages yet or few messages) */}
          {messages.length <= 1 && (
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Quick actions:</p>
              <div className="flex flex-wrap gap-2">
                {quickActions.map((action) => (
                  <button
                    key={action.label}
                    onClick={() => handleQuickAction(action.query)}
                    className="px-3 py-1.5 text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600
                      rounded-full hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:border-purple-300
                      text-gray-700 dark:text-gray-300 transition-colors touch-manipulation"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything about the shop..."
                  disabled={isLoading || !isConnected}
                  className="w-full px-4 py-3 pr-12 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl
                    text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400
                    focus:ring-2 focus:ring-purple-500 focus:bg-white dark:focus:bg-gray-700
                    disabled:opacity-50 disabled:cursor-not-allowed
                    text-sm touch-manipulation"
                />
                {/* Future: Voice input button */}
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-600
                    transition-colors touch-manipulation opacity-50 cursor-not-allowed"
                  title="Voice input (coming soon)"
                  disabled
                >
                  <Mic className="h-5 w-5" />
                </button>
              </div>
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading || !isConnected}
                className="p-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl
                  hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed
                  transition-all touch-manipulation min-w-[48px]"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>
  );
};

export default AIAssistantChat;
